import React from 'react';
import { Table, Typography, Tag, Collapse, Space } from 'antd';
import { 
  FaVial, 
  FaBolt, 
  FaStethoscope, 
  FaWeight,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';
import { COLORS } from '../../constants/colors';

const { Text, Title } = Typography;
const { Panel } = Collapse;

/**
 * Risk Scoring Settings - Displays how cardiovascular risk scores are calculated
 * This is a read-only reference for users to understand the scoring methodology
 */
const RiskScoringSettings = () => {
  // Vital Signs risk factors
  const vitalSignsFactors = [
    { factor: 'Stage 2 Hypertension', points: 25, condition: 'Systolic ≥180 or Diastolic ≥110 mmHg', description: 'Severe elevation requiring immediate attention' },
    { factor: 'Stage 1 Hypertension', points: 15, condition: 'Systolic ≥140 or Diastolic ≥90 mmHg', description: 'Elevated blood pressure requiring management' },
    { factor: 'Elevated Blood Pressure', points: 5, condition: 'Systolic ≥130 or Diastolic ≥80 mmHg', description: 'Pre-hypertension range' },
    { factor: 'Tachycardia', points: 5, condition: 'Heart Rate > 100 bpm', description: 'Elevated resting heart rate' },
    { factor: 'Bradycardia', points: 5, condition: 'Heart Rate < 50 bpm', description: 'Low resting heart rate' },
  ];

  // Body Measurements risk factors
  const bodyMeasurementFactors = [
    { factor: 'High WHR', points: 15, condition: 'WHR ≥ 1.0', description: 'High cardiovascular risk from central obesity' },
    { factor: 'Moderate WHR', points: 10, condition: 'WHR ≥ 0.90', description: 'Moderate risk from waist-to-hip ratio' },
    { factor: 'Elevated WHR', points: 5, condition: 'WHR ≥ 0.85', description: 'Mildly elevated risk' },
  ];

  // Blood Test risk factors
  const bloodTestFactors = [
    { factor: 'Diabetic Glucose (Fasting)', points: 20, condition: 'Fasting Glucose ≥ 126 mg/dL', description: 'Diabetes range' },
    { factor: 'Diabetic Glucose (Non-Fasting)', points: 20, condition: 'Random Glucose ≥ 200 mg/dL', description: 'Diabetes range' },
    { factor: 'Diabetic A1C', points: 20, condition: 'A1C ≥ 6.5%', description: 'Diabetes range hemoglobin A1C' },
    { factor: 'Very High Triglycerides', points: 20, condition: 'Triglycerides ≥ 500 mg/dL', description: 'Severe hypertriglyceridemia' },
    { factor: 'High Total Cholesterol', points: 15, condition: 'Total Cholesterol ≥ 240 mg/dL', description: 'High cholesterol level' },
    { factor: 'High Lipoprotein(a)', points: 15, condition: 'Lp(a) ≥ 125 nmol/L', description: 'Elevated genetic risk marker' },
    { factor: 'High Triglycerides', points: 12, condition: 'Triglycerides ≥ 200 mg/dL', description: 'High triglyceride level' },
    { factor: 'High LDL Cholesterol', points: 12, condition: 'LDL ≥ 160 mg/dL', description: 'High "bad" cholesterol' },
    { factor: 'Prediabetic Glucose', points: 10, condition: 'Fasting 100-125 or Random 140-199 mg/dL', description: 'Prediabetes range' },
    { factor: 'Prediabetic A1C', points: 10, condition: 'A1C 5.7-6.4%', description: 'Prediabetes range' },
    { factor: 'Low HDL Cholesterol', points: 10, condition: 'HDL < 40 mg/dL', description: 'Low "good" cholesterol - protective factor reduced' },
    { factor: 'Borderline High Total Cholesterol', points: 8, condition: 'Total Cholesterol 200-239 mg/dL', description: 'Borderline high' },
    { factor: 'Moderate Lipoprotein(a)', points: 8, condition: 'Lp(a) 75-124 nmol/L', description: 'Moderately elevated genetic marker' },
    { factor: 'Borderline High LDL', points: 8, condition: 'LDL 130-159 mg/dL', description: 'Borderline high LDL' },
    { factor: 'Borderline High Triglycerides', points: 6, condition: 'Triglycerides 150-199 mg/dL', description: 'Borderline elevated' },
    { factor: 'Above Optimal LDL', points: 4, condition: 'LDL 100-129 mg/dL', description: 'Above optimal but not high' },
  ];

  // EKG risk factors
  const ekgFactors = [
    { factor: 'Atrial Fibrillation on EKG', points: 25, condition: 'AFib detected', description: 'Irregular rhythm increasing stroke risk' },
    { factor: 'High QTc Prolongation', points: 20, condition: 'QTc ≥ 500 ms', description: 'High risk for torsades de pointes' },
    { factor: 'Abnormal EKG Interpretation', points: 15, condition: 'Abnormal or pathological findings', description: 'Significant EKG abnormalities' },
    { factor: 'Arrhythmia', points: 15, condition: 'Irregular rhythm detected', description: 'Non-AFib irregular rhythm' },
    { factor: 'Prolonged QTc', points: 10, condition: 'QTc 450-499 ms', description: 'Moderately prolonged QT interval' },
    { factor: 'Tachycardia/Bradycardia on EKG', points: 8, condition: 'Rate abnormality on EKG', description: 'Heart rate outside normal range' },
    { factor: 'Short QTc', points: 8, condition: 'QTc ≤ 350 ms', description: 'Short QT syndrome risk' },
    { factor: 'Borderline EKG', points: 8, condition: 'Borderline or nonspecific changes', description: 'Minor EKG changes to monitor' },
  ];

  // Risk level thresholds
  const riskLevels = [
    { level: 'Critical', threshold: '≥ 80 points', color: '#ff4d4f', description: 'Immediate medical attention recommended' },
    { level: 'High', threshold: '50-79 points', color: '#fa8c16', description: 'Significant cardiovascular risk - follow-up needed' },
    { level: 'Medium', threshold: '25-49 points', color: '#faad14', description: 'Moderate risk - lifestyle modifications advised' },
    { level: 'Low', threshold: '8-24 points', color: '#52c41a', description: 'Low risk - continue healthy habits' },
    { level: 'Minimal', threshold: '0-7 points', color: '#08979c', description: 'Minimal risk factors detected' },
    { level: 'No Data', threshold: 'No test results', color: '#8c8c8c', description: 'No medical testing results recorded yet - not scored' },
  ];

  const columns = [
    {
      title: 'Risk Factor',
      dataIndex: 'factor',
      key: 'factor',
      width: '30%',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: '15%',
      render: (points) => (
        <Tag color={points >= 20 ? 'red' : points >= 10 ? 'orange' : 'blue'}>
          +{points}
        </Tag>
      ),
      sorter: (a, b) => b.points - a.points
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      width: '25%',
      render: (text) => text ? <Text code>{text}</Text> : '-'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => <Text type="secondary">{text}</Text>
    }
  ];

  const riskLevelColumns = [
    {
      title: 'Risk Level',
      dataIndex: 'level',
      key: 'level',
      width: '20%',
      render: (text, record) => (
        <Tag color={record.color} style={{ fontWeight: 600 }}>
          {text}
        </Tag>
      )
    },
    {
      title: 'Score Threshold',
      dataIndex: 'threshold',
      key: 'threshold',
      width: '25%',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Interpretation',
      dataIndex: 'description',
      key: 'description',
      render: (text) => <Text>{text}</Text>
    }
  ];

  const panelStyle = {
    marginBottom: 16,
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #f0f0f0'
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          <FaExclamationTriangle style={{ marginRight: 8, color: COLORS.$mainRed }} />
          Risk Score Classification
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Total risk points are summed and classified into the following levels:
        </Text>
        <Table
          dataSource={riskLevels}
          columns={riskLevelColumns}
          pagination={false}
          size="small"
          rowKey="level"
          style={{ marginBottom: 24 }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          * Raw scores are normalized to a 0-100 scale for display (score ÷ 200 × 100, capped at 100)
        </Text>
      </div>

      <Title level={5} style={{ marginBottom: 16 }}>
        Risk Factor Categories
      </Title>

      <Collapse 
        defaultActiveKey={['vital-signs']}
        expandIconPosition="end"
        style={{ background: 'transparent', border: 'none' }}
      >
        <Panel 
          header={
            <Space>
              <FaStethoscope style={{ color: '#1890ff' }} />
              <Text strong>Vital Signs</Text>
              <Tag>Max: 30 pts</Tag>
            </Space>
          }
          key="vital-signs"
          style={panelStyle}
        >
          <Table
            dataSource={vitalSignsFactors}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="factor"
          />
        </Panel>

        <Panel 
          header={
            <Space>
              <FaWeight style={{ color: '#fa8c16' }} />
              <Text strong>Body Measurements (WHR)</Text>
              <Tag>Max: 15 pts</Tag>
            </Space>
          }
          key="body-measurements"
          style={panelStyle}
        >
          <Table
            dataSource={bodyMeasurementFactors}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="factor"
          />
        </Panel>

        <Panel 
          header={
            <Space>
              <FaVial style={{ color: '#52c41a' }} />
              <Text strong>Blood Tests</Text>
              <Tag>Max: 112 pts</Tag>
            </Space>
          }
          key="blood-tests"
          style={panelStyle}
        >
          <Table
            dataSource={bloodTestFactors}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="factor"
          />
        </Panel>

        <Panel 
          header={
            <Space>
              <FaBolt style={{ color: '#eb2f96' }} />
              <Text strong>EKG/ECG Tests</Text>
              <Tag>Max: 60 pts</Tag>
            </Space>
          }
          key="ekg-tests"
          style={panelStyle}
        >
          <Table
            dataSource={ekgFactors}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="factor"
          />
        </Panel>
      </Collapse>

      <div style={{ marginTop: 24, padding: 16, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
        <Text strong style={{ color: '#52c41a' }}>
          <FaInfoCircle style={{ marginRight: 8 }} />
          How the Score Works
        </Text>
        <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
          <li>Points are <strong>cumulative</strong> - all applicable risk factors are summed together</li>
          <li>Only <strong>medical testing results</strong> are scored (blood pressure, finger prick, blood draw, EKG, WHR)</li>
          <li>The score uses the <strong>most recent visit that has test results</strong>; empty check-in visits are skipped</li>
          <li>Self-reported medical history and age do <strong>not</strong> affect the score</li>
          <li>Members with no test results show as <strong>No Data</strong> instead of a risk level</li>
          <li>A single <strong>severe finding</strong> (BP ≥ 180/110, AFib on EKG, or QTc ≥ 500 ms) raises the score to at least <strong>50 (High)</strong></li>
          <li>At-home lab kit results count as blood-draw results for the visit they were collected at</li>
        </ul>
      </div>
    </div>
  );
};

export default RiskScoringSettings;
